<template>
  <div class="color-block">
    <div
      v-if="configsValue.label !== ''"
      class="flex justify-content-between flex-wrap"
    >
      <div class="flex justify-content-center">
        <label class="block my-3">
          {{ t(configsValue.label) }}
        </label>
      </div>
      <div class="flex justify-content-center align-items-center">
        <ToggleDisplaySettings
          v-if="!Object.prototype.hasOwnProperty.call(configsValue, 'displayToggle') || configsValue.displayToggle"
          :configs="configsValue"
          :parser-values="parserValues"
          :element-part-type="elementPartType"
          @display-settings="(value) => displayProperties = value"
        />
      </div>
    </div>
    <div
      class="p-inputgroup"
      :class="{ 'hidden': !displayProperties }"
    >
      <InputText
        v-model="selectedColor"
        class="p-inputtext-sm color-input"
        :disabled="isTransparent"
        @change="filterInputColor"
        @keyup="filterInputColor"
      />
      <span
        class="p-inputgroup-addon button-block"
        @click="toggleColorPicker"
      >
        <Button
          class="p-inputgroup-addon p-button-sm color-button"
          :class="{ 'checkerboard': isTransparent }"
          :style="!isTransparent ? { 'background': selectedColor } : {}"
        />
        <i
          class="far fa-arrow-down p-inputgroup-addon text-center button-icon"
        />
      </span>
      <span
        v-if="configsValue.options.allowTransparent"
        v-tooltip="t('templateBuilder.transparentColorTooltip')"
        class="p-inputgroup-addon transparent-button-block"
        @click="toggleTransparent"
      >
        <i
          class="fas fa-ban text-center transparent-icon"
          :class="{ 'active': isTransparent }"
        />
      </span>
    </div>
    <OverlayPanel
      ref="overlayPanelRef"
    >
      <CustomColorPicker
        data-test-id="color-picker"
        :color="selectedColor"
        :color-format="colorFormat"
        :show-alpha="configsValue.options.showAlpha"
        :show-gradient="configsValue.options.showGradient"
        title="Color Picker"
        @color-changed="changeColor"
      />
    </OverlayPanel>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, Ref, ref, watch, onBeforeUnmount, computed,
} from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

import {
  ColorFieldConfig, ColorFormatEnum, ParserFieldObject, Property,
} from '@/types';
import { useI18n } from 'vue-i18n';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'Color',
  directives: {
    tooltip: Tooltip,
  },

  components: {
    ToggleDisplaySettings,
    CustomColorPicker,
    OverlayPanel,
    InputText,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<ColorFieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const configsValue = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configsValue.value, 'displayToggle') || configsValue.value.displayToggle === true));
    const VALID_COLOR_REGEX_RULE = /^(#([\da-f]{3}){1,2}$)|(rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(,\s*(\d+|\d*\.\d+))?\s*\))$|((repeating-)?(linear|radial)-gradient\(.*\))$/i;
    const overlayPanelRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const property: Ref<Property> = ref(props.configs.properties && props.configs.properties[0]);
    const selectedColor: Ref<string> = ref(props.configs.options.defaultValue);

    const transparentValues = ['transparent', 'rgba(0, 0, 0, 0)', ''];
    const isTransparent = ref(false);
    const allowTransparent = computed(() => configsValue.value.options.allowTransparent ?? false);
    const transparentValue = computed(() => configsValue.value.options.transparentValue ?? 'transparent');

    const isValidColor = (color: string) => {
      if (allowTransparent.value && transparentValues.includes(color)) {
        return true;
      }
      return VALID_COLOR_REGEX_RULE.test(color);
    };

    const isTransparentValue = (color: string) => transparentValues.includes(color);

    watch(() => props.parserValues, (values, oldValues) => {
      if (values.selector !== oldValues?.selector) {
        if (property.value) {
          if (isTransparentValue(property.value.value) && allowTransparent.value) {
            isTransparent.value = true;
            selectedColor.value = t('templateBuilder.transparent');
          } else {
            isTransparent.value = false;
            selectedColor.value = isValidColor(property.value.value) ? property.value.value : props.configs.options.defaultValue;
          }
        }
      }
    }, { immediate: true });

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };
    const emitValuesChanges = () => {
      if (property.value.value !== selectedColor.value) {
        property.value.value = selectedColor.value;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [property.value],
        });
      }
    };
    const changeColor = (color: string) => {
      if (isTransparent.value) {
        isTransparent.value = false;
      }
      selectedColor.value = color;
      emitValuesChanges();
    };
    const filterInputColor = () => {
      if (isValidColor(selectedColor.value)) {
        emitValuesChanges();
      }
    };
    const toggleTransparent = () => {
      isTransparent.value = !isTransparent.value;
      if (isTransparent.value) {
        selectedColor.value = transparentValue.value;
        emitValuesChanges();
        selectedColor.value = t('templateBuilder.transparent');
      } else {
        selectedColor.value = props.configs.options.defaultValue;
        emitValuesChanges();
      }
    };

    onBeforeUnmount(() => {
      overlayPanelRef.value = null;
    });

    return {
      displayProperties,
      selectedColor,
      isTransparent,
      overlayPanelRef,
      colorFormat,
      toggleColorPicker,
      toggleTransparent,
      changeColor,
      filterInputColor,
      configsValue,
      t,
    };
  },
});
</script>

<style scoped lang="scss">
.color-block {
  .color-input {
    border-color: $montana;
    font-size: 1rem;

    &:hover {
      border-color: $brand-color-primary;
    }

    &:enabled:focus,
    &:enabled:active {
      border: 1px solid $brand-color-primary;
      box-shadow: none;
    }
  }

  .button-block {
    background-color: $white;
    border-color: $heather;
    padding: 0;

    &:last-child {
      border-color: $heather;
      border-top-right-radius: $field-border-radius;
      border-bottom-right-radius: $field-border-radius;
    }

    .color-button {
      margin: 0.25rem 0 0.25rem 0.25rem;
      min-width: 1rem;
      width: 1rem;
      height: 1rem;
      padding: 0;
      border-color: $heather;

      &:enabled:focus,
      &:enabled:active {
        border: 1px solid $brand-color-primary;
        box-shadow: none;
      }
    }

    .button-icon {
      background-color: $white;
      border: none;
      padding: .25rem;
      color: $heather;
      cursor: pointer;
    }

    &:hover {
      border-color: $brand-color-primary;
    }
  }

  .transparent-button-block {
    background-color: $white;
    border-color: $heather;
    padding: 0 0.4rem;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      border-color: $brand-color-primary;
    }

    .transparent-icon {
      color: $heather;
      font-size: 0.85rem;
      transition: color 0.2s;

      &.active {
        color: $brand-color-primary;
      }
    }
  }

  .color-button.checkerboard {
    background: repeating-conic-gradient(#c0c0c0 0% 25%, #ffffff 0% 50%) 50% / 8px 8px !important;
    border-color: $heather;
  }
}
</style>
